import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  DxButtonModule,
  DxCheckBoxModule,
  DxFormModule,
  DxNumberBoxModule,
  DxSelectBoxModule,
  DxTagBoxModule,
  DxTextBoxModule,
} from "devextreme-angular";
import {
  Color,
  ConfigTrafficLightItem,
  ConfigTrafficLightResultModel,
  TrafficLightTimelineConfig,
} from "src/app/interfaces/entities/traffic-light";
import { ConfigService } from "src/app/services/config.service";
import { AppGlobals } from "src/app/app.globals";
import { TimelineComponent } from "../trafficlight-timeline/timeline-component";

import { ActivityLogService } from "src/app/services/activity-log.service";
import { ActivitySection } from "src/app/constants/activity-log";
import { SearchableDropdownDirective } from '../../../directives/searchable-dropdown.directive';
import { GpdButtonComponent } from "src/app/components/gpd-button/gpd-button.component";
@Component({
    selector: "app-config-remuneration-type.page",
    imports: [
    GpdButtonComponent,
        CommonModule,
        DxTextBoxModule,
        DxFormModule,
        DxButtonModule,
        DxNumberBoxModule,
        DxCheckBoxModule,
        DxSelectBoxModule,
        DxTagBoxModule,
        TimelineComponent,
        SearchableDropdownDirective,
    ],
    templateUrl: "./config-traffic-light.page.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ["./config-traffic-light.page.css"]
})
export class ConfigTrafficLightPageComponent implements OnInit {
  trafficLightItems: ConfigTrafficLightItem = {
    greenLimitDays: 0,
    greenBefore: true,
    yellowLimitDays: 0,
    yellowBefore: true,
    redLimitDays: 0,
    redBefore: false,
    reminderVisible: false,
  };

  trafficLightTimeline: TrafficLightTimelineConfig[] = [];

  required = $localize`:@@required:Required`;
  days = $localize`:@@days:Days`;

  directions = [
    { label: $localize`:@@before:Before`, value: true },
    { label: $localize`:@@after:After`, value: false },
  ];

  constructor(private configService: ConfigService, private activityLog: ActivityLogService) {}

  ngOnInit(): void {
    this.activityLog.logView({ Section: ActivitySection.CONFIG_TRAFFIC_LIGHT });
    this.loadTrafficConfigData();
  }

  loadTrafficConfigData(): void {
    this.configService
      .getTrafficLightConfig()
      .then((result: ConfigTrafficLightResultModel) => {
        if (result.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback);
        }

        this.trafficLightItems = result.configTrafficLightItem;
        this.prettifyData(true);
        this.propagateChanges();
      });
  }

  validateLimits(): boolean {
    if (!this.trafficLightItems) {
      return false;
    }
    this.prettifyData(false); // convert to negative values for validation

    const { greenLimitDays, yellowLimitDays, redLimitDays } =
      this.trafficLightItems;

    if (greenLimitDays <= yellowLimitDays) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@invalidLimitsGreenYellow:Green limit days must be greater than yellow limit days.`,
      );
      this.prettifyData(true);
      return false;
    }

    if (yellowLimitDays <= redLimitDays) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@invalidLimitsYellow:Yellow limit days must be greater than red limit days.`,
      );
      this.prettifyData(true);
      return false;
    }

    if (greenLimitDays <= redLimitDays) {
      AppGlobals.ShowErrorMessage(
        $localize`:@@invalidLimitsGreenRed:Green limit days must be greater than red limit days.`,
      );
      this.prettifyData(true);
      return false;
    }

    this.prettifyData(true);
    return true;
  }

  saveTrafficConfigurations(e: any): void {
    if (!this.validateLimits()) {
      return;
    }
    this.prettifyData(false);

    this.configService
      .saveTrafficLightConfig({
        configTrafficLightItem: this.trafficLightItems,
      })
      .then((result: ConfigTrafficLightResultModel) => {
        if (result.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback);
          return;
        }
        this.prettifyData(true); // don't display negative values in UI
      });
  }

  prettifyData(hideNegativeValues: boolean): void {
    const limits = [
      { direction: "greenBefore", days: "greenLimitDays" },
      { direction: "yellowBefore", days: "yellowLimitDays" },
      { direction: "redBefore", days: "redLimitDays" },
    ];

    if (hideNegativeValues) {
      for (const { direction, days } of limits) {
        if (this.trafficLightItems[days]) {
          this.trafficLightItems[direction] =
            this.trafficLightItems[days] < 0 ? false : true;
          if (!this.trafficLightItems[direction])
            this.trafficLightItems[days] = Math.abs(
              this.trafficLightItems[days],
            );
        } else {
          this.trafficLightItems[direction] = true;
          this.trafficLightItems[days] = 0;
        }
      }
    } else {
      for (const { direction, days } of limits) {
        if (!this.trafficLightItems[direction]) {
          this.trafficLightItems[days] = -Math.abs(
            this.trafficLightItems[days],
          );
        }
      }
    }
  }

  updateTimeline(e: any) {
    let type = e.event?.target?.value;
    if (type) {
      this.propagateChanges();
    }
  }

  propagateChanges() {
    this.trafficLightTimeline = [
      {
        id: 0,
        color: Color.Green,
        days: this.trafficLightItems.greenLimitDays,
        before: this.trafficLightItems.greenBefore,
        text: this.trafficLightItems.greenLimitDays + " " + this.days,
      },
      {
        id: 1,
        color: Color.Yellow,
        days: this.trafficLightItems.yellowLimitDays,
        before: this.trafficLightItems.yellowBefore,
        text: this.trafficLightItems.yellowLimitDays + " " + this.days,
      },
      {
        id: 2,
        color: Color.Red,
        days: this.trafficLightItems.redLimitDays,
        before: this.trafficLightItems.redBefore,
        text: this.trafficLightItems.redLimitDays + " " + this.days,
      },
    ];
  }
}
